import type {
  AnalyticsSummary,
  AppSetting,
  Contract,
  ContractHistoryResponse,
  ReviewEntry,
  ReviewWithDisplay,
  SessionStatus,
  Solution,
  User,
} from "../shared/types";

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorBody = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(errorBody?.error || "Request failed.", response.status);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json() as Promise<T>;
  }
  return (response.text() as unknown) as T;
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    headers: {
      ...(init?.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(init?.headers || {}),
    },
    ...init,
  });

  return parseResponse<T>(response);
}

export const api = {
  unlock: (secretPhrase: string) =>
    apiFetch<{ unlocked: boolean; secretPhraseVersion: number }>("/api/session/unlock", {
      method: "POST",
      body: JSON.stringify({ secretPhrase }),
    }),
  lock: () => apiFetch<{ unlocked: boolean }>("/api/session/lock", { method: "POST" }),
  sessionStatus: () => apiFetch<SessionStatus>("/api/session/status"),
  selectUser: (userId: string | null) =>
    apiFetch<{ selectedUserId: string | null }>("/api/session/select-user", {
      method: "POST",
      body: JSON.stringify({ userId }),
    }),
  listUsers: () => apiFetch<User[]>("/api/users"),
  createUser: (payload: Pick<User, "firstName" | "lastName">) =>
    apiFetch<User>("/api/users", { method: "POST", body: JSON.stringify(payload) }),
  updateUser: (id: string, payload: Partial<Pick<User, "firstName" | "lastName" | "isArchived">>) =>
    apiFetch<User>(`/api/users/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteUser: (id: string) => apiFetch<{ action: "archived" | "deleted" }>(`/api/users/${id}`, { method: "DELETE" }),
  listContracts: () => apiFetch<Contract[]>("/api/contracts"),
  createContract: (payload: Pick<Contract, "contractName" | "clientName">) =>
    apiFetch<Contract>("/api/contracts", { method: "POST", body: JSON.stringify(payload) }),
  updateContract: (id: string, payload: Partial<Pick<Contract, "contractName" | "clientName" | "isArchived">>) =>
    apiFetch<Contract>(`/api/contracts/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteContract: (id: string) =>
    apiFetch<{ action: "archived" | "deleted" }>(`/api/contracts/${id}`, { method: "DELETE" }),
  contractHistory: (id: string) => apiFetch<ContractHistoryResponse>(`/api/contracts/${id}/history`),
  listSolutions: () => apiFetch<Solution[]>("/api/solutions"),
  createSolution: (payload: Pick<Solution, "softwareName">) =>
    apiFetch<Solution>("/api/solutions", { method: "POST", body: JSON.stringify(payload) }),
  updateSolution: (id: string, payload: Partial<Pick<Solution, "softwareName" | "isArchived">>) =>
    apiFetch<Solution>(`/api/solutions/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteSolution: (id: string) =>
    apiFetch<{ action: "archived" | "deleted" }>(`/api/solutions/${id}`, { method: "DELETE" }),
  getActiveReview: (userId: string) => apiFetch<ReviewWithDisplay | null>(`/api/reviews/active/${userId}`),
  startReview: (payload: { userId: string; contractId: string; mode: "manual" | "software"; solutionId?: string | null }) =>
    apiFetch<ReviewWithDisplay>("/api/reviews/start", { method: "POST", body: JSON.stringify(payload) }),
  pauseReview: (id: string) => apiFetch<ReviewWithDisplay>(`/api/reviews/${id}/pause`, { method: "POST" }),
  resumeReview: (id: string) => apiFetch<ReviewWithDisplay>(`/api/reviews/${id}/resume`, { method: "POST" }),
  stopReview: (id: string) => apiFetch<ReviewWithDisplay>(`/api/reviews/${id}/stop`, { method: "POST" }),
  saveReview: (id: string) => apiFetch<ReviewEntry>(`/api/reviews/${id}/save`, { method: "POST" }),
  discardReview: (id: string) => apiFetch<{ discarded: boolean }>(`/api/reviews/${id}/discard`, { method: "POST" }),
  deleteReview: (id: string) => apiFetch<{ deleted: boolean }>(`/api/reviews/${id}`, { method: "DELETE" }),
  analytics: () => apiFetch<AnalyticsSummary>("/api/analytics/summary"),
  exportEntity: async (entity: "users" | "contracts" | "solutions" | "reviews") => {
    const response = await fetch(`/api/export/${entity}`, { credentials: "include" });
    if (!response.ok) {
      throw new ApiError("Export failed.", response.status);
    }
    return response.text();
  },
  importEntity: (entity: "users" | "contracts" | "solutions", csvText: string) =>
    apiFetch<{ imported: number }>(`/api/import/${entity}`, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: csvText,
    }),
  updateSecretPhrase: (secretPhrase: string) =>
    apiFetch<AppSetting>("/api/settings/secret-phrase", {
      method: "PATCH",
      body: JSON.stringify({ secretPhrase }),
    }),
};
